import { useTranslation } from "i18next-vue";
import gql from "fraql";
import { computed, ref } from "vue";
// @ts-ignore
import AppFileUploadSkeleton from "@elements/AppFileUploadSkeleton.vue";
// property fields considered as documents for horse application
// also used as document cateogry placement ordering
export const HorseApplicationDocumentKeys = [
    "attachedFullDocuments",
    "attachedNatPassportNamePages",
    "attachedNatPassportInfoPages",
    "attachedNatPassportDiagramPages",
    "attachedNatPassportPedigreePages",
    "attachedNatPassportOwnerPages",
    "attachedOwnershipProofs",
    "attachedFeiNamePages",
    "attachedFeiOwnerPages",
    "attachedPonyMeasurementProofs",
    "attachedVaccinationProofs",
    "documents"
];
export const HorseApplicationDocFragment = gql `
  fragment _ on HorseApplication {
    attachedNatPassportNamePages {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedNatPassportInfoPages {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedNatPassportDiagramPages {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedNatPassportPedigreePages {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedNatPassportOwnerPages {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedOwnershipProofs {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedFullDocuments {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedFeiNamePages {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedFeiOwnerPages {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedPonyMeasurementProofs {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
    attachedVaccinationProofs {
      id
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }

    documents {
      ${AppFileUploadSkeleton.fragments.documentFields}
      url
    }
  }
`;
export function useHorseApplicationDocumentsHelper() {
    const { t } = useTranslation();
    const horseDocumentCategoryLabels = computed(() => {
        return {
            attachedNatPassportNamePages: t("horses:horseDocuments.namePageNatDoc.subheading", "Name page of national document"),
            attachedNatPassportInfoPages: t("horses:horseDocuments.generalInformationPage.subheading", "General information page(s) of national document"),
            attachedNatPassportDiagramPages: t("horses:horseDocuments.diagramPage.subheading", "Description and diagram page(s)"),
            attachedNatPassportPedigreePages: t("horses:horseDocuments.pedigreePage.subheading", "Pedigree"),
            attachedNatPassportOwnerPages: t("horses:horseDocuments.ownerPageNatDoc.subheading", "Ownership page(s) of national document"),
            attachedOwnershipProofs: t("horses:horseDocuments.proofOfOwnership.subheading", "Horse purchase contract or other legal proof of ownership"),
            attachedFullDocuments: t("horses:horseDocuments.fullDocument.heading", "Full document"),
            attachedFeiNamePages: t("horses:horseDocuments.FEINamePage.subheading", "Name page of FEI RC or passport"),
            attachedFeiOwnerPages: t("horses:horseDocuments.FEIOwnerPage.subheading", "Owner page of FEI RC or passport"),
            attachedPonyMeasurementProofs: t("horses:horseDocuments.ponyMeasurementCertificate.subheading", "Pony measurement certificate"),
            attachedVaccinationProofs: t("horses:horseDocuments.vaccinationsPage.subheading", "Vaccinations pages"),
            documents: t("horses:horseDocuments.otherPage.subheading", "Other documents")
        };
    });
    const horseDocumentsCategoryFormLabels = computed(() => {
        return {
            attachedNatPassportNamePages: t("horses:horseApplication.documentsSection.namePageNatDoc.subheading", "Name page of national document"),
            attachedNatPassportInfoPages: t("horses:horseApplication.documentsSection.generalInformationPage.subheading", "General information page(s) of national document"),
            attachedNatPassportDiagramPages: t("horses:horseApplication.documentsSection.diagramPage.subheading", "Description and diagram page(s)"),
            attachedNatPassportPedigreePages: t("horses:horseApplication.documentsSection.pedigreePage.subheading", "Pedigree"),
            attachedNatPassportOwnerPages: t("horses:horseApplication.documentsSection.ownerPageNatDoc.subheading", "Ownership page(s) of national document"),
            attachedOwnershipProofs: t("horses:horseApplication.documentsSection.proofOfOwnership.subheading", "Horse purchase contract or other legal proof of ownership"),
            attachedFullDocuments: t("horses:horseApplication.documentsSection.fullDocument.heading", "Full document"),
            attachedFeiNamePages: t("horses:horseApplication.documentsSection.FEINamePage.subheading", "Name page of FEI RC or passport"),
            attachedFeiOwnerPages: t("horses:horseApplication.documentsSection.FEIOwnerPage.subheading", "Owner page of FEI RC or passport"),
            attachedPonyMeasurementProofs: t("horses:horseApplication.documentsSection.ponyMeasurementCertificate.subheading", "Pony measurement certificate"),
            attachedVaccinationProofs: t("horses:horseApplication.documentsSection.vaccinationsPage.subheading", "Vaccinations pages"),
            documents: t("horses:horseApplication.documentsSection.otherPage.subheading", "Other documents")
        };
    });
    const horseDocumentKeys = computed(() => {
        return HorseApplicationDocumentKeys;
    });
    function horseDocumentsOrdered(horseApp) {
        const docs = [];
        horseDocumentKeys.value.forEach(documentKey => {
            if (horseApp[documentKey]) {
                docs[documentKey] = horseApp[documentKey];
            }
        });
        return Object.entries(docs).sort(([a], [b]) => horseDocumentKeys.value.indexOf(a) - horseDocumentKeys.value.indexOf(b));
    }
    function horseDocumentsAll(horseApp) {
        return horseDocumentsOrdered(horseApp)
            .map(([, docs]) => docs)
            .flat();
    }
    const horseDocumentAcceptableFormats = ref(".jpg,.jpeg,.png,.pdf,.bmp");
    return {
        horseDocumentsCategoryFormLabels,
        horseDocumentCategoryLabels,
        horseDocumentKeys,
        horseDocumentsOrdered,
        horseDocumentsAll,
        horseDocumentAcceptableFormats
    };
}
import { replaceRequiredFileToRequiredIf } from "@elements/AppFileUploadSkeleton.vue";
/**
 * Function that sets exception if attachedFullDocuments or its Uploaded property has an upload then rest of docs
 * will simply have required validation rule be deleted
 *
 * @param {*} validation validation for an field
 * @param {*} formField
 * @param {*} siblings typical form object
 */
export function requiredHorseDocumentUnlessFullDocumentPresent(validation, formField, siblings) {
    if ((siblings["attachedFullDocuments"]?.length > 0 ||
        siblings["attachedFullDocumentsUploaded"]?.length > 0) &&
        formField.name != "attachedFullDocuments") {
        delete validation.required;
        return validation;
    }
    return replaceRequiredFileToRequiredIf(validation, formField, siblings);
}
